import { ToCodePipe } from './../../../pipes/toCode.pipe';
import { Component, forwardRef, Input, ElementRef, OnInit, HostBinding } from '@angular/core';
import { AbstractControl, ControlValueAccessor, NgForm, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors } from '@angular/forms';

export interface BbCheckboxListOption {
	label: string;
	value: string;
	icon: string;
	iconText?: string;
	active?: boolean;
	disabled?: boolean;
	hidden?: boolean;
}

export interface ExtraColumnBbLabelCodeList {
	header: string;
	field: string;
	type: 'number'; // only number usecase for now
	required: boolean;
}
@Component({
	selector: 'bb-label-code-list',
	templateUrl: './bb-label-code-list.component.html',
	styleUrls: ['./bb-label-code-list.component.scss'],
	providers: [{
		provide: NG_VALUE_ACCESSOR,
		useExisting: forwardRef(() => BbLabelCodeListComponent),
		multi: true
	},
	{
		provide: NG_VALIDATORS,
		multi: true,
		useExisting: BbLabelCodeListComponent
	}]

})

export class BbLabelCodeListComponent implements ControlValueAccessor, OnInit {
	@Input() codeProp = 'value';
	@Input() theme: 'dark' | 'light' = 'light';
	@Input() withPreview = false;
	@Input() form: NgForm;
	@Input() showActiveCheckbox = false; // display checkbox in front (listening to option.active)
	@Input() extraColumns?: ExtraColumnBbLabelCodeList[];

	editMode = false;
	modelValue: any = [];
	useCustomValues = false;
	initialNumValues = 0;
	modelChanged = false;

	constructor(
		private toCodePipe: ToCodePipe,
		private elRef: ElementRef
	) { }

	ngOnInit() { }

	@HostBinding('class.dark-theme') get isDarkTheme() {
		return this.theme === 'dark';
	}

	setEditMode(editMode: boolean) {
		if (editMode && !this.modelValue?.length) {
			this.modelValue = [{
				label: ''
			}];
			this.modelValue[0][this.codeProp] = '';
		}
		this.editMode = editMode;
		if (!editMode && this.modelChanged) {
			this.propagateChange(this.modelValue);
			this.modelChanged = false;
		}
	}

	update() {
		if (this.modelValue) {
			this.initialNumValues = this.modelValue.length;
		}
	}

	modelChange() {
		if (this.withPreview) {
			// will propagate when preview is closed
			this.modelChanged = true;
		} else {
			this.propagateChange(this.modelValue);
		}
	}

	removeOption(index: number) {
		this.modelValue.splice(index, 1);
		this.update();
		this.modelChange();
	}

	optionValueChange(index: number) {
		if (index >= this.initialNumValues) {
			this.modelValue[index][this.codeProp] = this.toCodePipe.transform(this.modelValue[index].label);
		}
		this.modelChange();
	}

	optionCodeChange(index: number) {
		this.modelValue[index][this.codeProp] = this.toCodePipe.transform(this.modelValue[index][this.codeProp]);
		this.modelChange();
	}

	addOption() {
		if (!this.modelValue) {
			this.modelValue = [];
		}
		const newOption = {
			label: '',
			active: true
		};
		newOption[this.codeProp] = '';

		if (!this.showActiveCheckbox) {
			delete newOption.active;
		}

		this.modelValue.push(newOption);
		this.modelChange();
		setTimeout(() => {
			this.focusLastLabelField();
		});
	}

	focusLastLabelField() {
		const labelFields = this.elRef.nativeElement.querySelectorAll('.list-items .label-field');
		const lastLabelField = labelFields[labelFields.length - 1];
		if (lastLabelField) {
			lastLabelField.focus();
		}
	}

	onLabelFieldEnter() {
		this.addOption();
	}

	// ControlValueAccessor fields:
	writeValue(value: string[]) {
		if (JSON.stringify(value) !== JSON.stringify(this.modelValue)) {
			this.modelValue = value;
			this.update();
		}
	}

	propagateChange = (_: any) => { };

	registerOnChange(fn: any) {
		this.propagateChange = fn;
	}

	registerOnTouched() {

	}

	validate(control: AbstractControl): ValidationErrors | null {
		const seen = new Set();

		const duplicateCode = control.value?.some(currentObject => seen.size === seen.add(currentObject[this.codeProp]).size);

		if (duplicateCode) {
			return { duplicate: true };
		}

		return null;
	}
}
